import { readSharedArticle } from 'api/teams'
import AdminSearchResultDrawer from 'components/drawer/search/AdminSearchResultDrawer'
import ContactUsModal from 'components/modals/ContactUsModal'
import NotificationSys from 'components/NotificationSystem'
import { TeamsContext } from 'features/Timeline/hooks/useTeams'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { routes } from 'routes/routes'
import { ArticleType, FilterModel, QuestioningType } from 'types'
import { noop } from 'utils'
import { SharedArticleDrawer } from '../../features/Share/components/SharedArticleDrawer/SharedArticleDrawer'
import { MySubscriptionDrawer } from '../../features/Subscription'
import { useShowControl } from '../../hooks/useShowControl'
import AboutUsDrawer from '../drawer/about-us/AboutUsDrawer'
import ViewArticleDrawer from '../drawer/article/view/ViewArticleDrawer'
import { ChangePasswordDrawer } from '../drawer/change-password/ChangePasswordDrawer'
import PersonOfConcernProfileDrawer from '../drawer/concern-profile/PersonOfConcernProfileDrawer'
import EditNoteDrawer from '../drawer/note/EditNoteDrawer'
import ViewNoteDrawer from '../drawer/note/ViewNoteDrawer'
import ProfileDrawer from '../drawer/profile/ProfileDrawer'
import SearchResultDrawer from '../drawer/search/SearchResultDrawer'
import TeamMemberProfileDrawer from '../drawer/TeamMemberProfileDrawer'
import AddMemberModal from '../modals/AddMemberModal'
import InviteModal from '../modals/InviteModal'
import QuestionWizard from '../modals/questionare/question-wizard'
import { useAuthContext } from './AuthProvider'

interface Props {
  children: React.ReactNode
}

const defaultValue = {
  handleOpenProfile: noop,
  handleOpenConcernProfile: noop,
  handleOpenNoteView: noop,
  handleOpenNoteEdit: noop,
  handleOpenQuestioning: noop,
  handleOpenSearchDrawer: noop,
  handleCloseSearchDrawer: noop,
  handleOpenAdminSearchDrawer: noop,
  handleCloseAdminSearchDrawer: noop,
  handleOpenInviteMember: noop,
  handleOpenAddMember: noop,
  handleOpenAboutUsDrawer: noop,
  handleOpenChangePasswordDrawer: noop,
  handleOpenTeamMemberDrawer: noop,
  handleOpenContactUs: noop,
  handleOpenMySubscription: noop,
}

type QuestionerProps = {
  id: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraParameters?: any
  type: QuestioningType
  mode: 'take' | 'view_results'
  onQuestioningTaken?: () => void
}

type DrawersContextType = {
  handleOpenProfile: (id?: number) => void
  /** Opens POC profile for the active care team (id from TeamsProvider, stays in sync when the team changes). */
  handleOpenConcernProfile: () => void
  handleOpenNoteView: (id: number, timelineId: number, isShared: boolean) => void
  handleOpenNoteEdit: (id?: number) => void
  handleOpenQuestioning: (props?: QuestionerProps) => void
  handleOpenSearchDrawer: (searchText: string, filter: FilterModel | null) => void
  handleCloseSearchDrawer: () => void
  handleOpenAdminSearchDrawer: (searchText: string, filter: FilterModel | null) => void
  handleCloseAdminSearchDrawer: () => void
  handleOpenInviteMember: () => void
  handleOpenAddMember: (id?: number) => void
  handleOpenAboutUsDrawer: () => void
  handleOpenChangePasswordDrawer: () => void
  handleOpenTeamMemberDrawer: (id: number, isCanEdit: boolean) => void
  handleOpenContactUs: () => void
  handleOpenMySubscription: () => void
}

const DrawersContext = React.createContext<DrawersContextType>(defaultValue)

export const useDrawersContext = () => {
  return useContext(DrawersContext)
}

const useQuestioningControl = (): [
  boolean,
  (props?: QuestionerProps) => void,
  () => void,
  QuestionerProps | undefined,
] => {
  const [props, setProps] = useState<QuestionerProps | undefined>()
  const [state, setState] = useState<boolean>(false)

  const onOpen = useCallback((props?: QuestionerProps) => {
    setState(true)
    setProps(props)
  }, [])

  const onClose = useCallback(() => {
    setState(false)
    setProps(undefined)
  }, [])

  return [state, onOpen, onClose, props]
}

const useShowIDState = (): [boolean, number | undefined, (id?: number) => void, () => void] => {
  const [isShow, setShow] = useState(false)
  const [id, setId] = useState<number>()

  const onOpen = (id?: number) => {
    setShow(true)
    setId(id)
  }

  const onClose = () => {
    setShow(false)
    setId(undefined)
  }

  return [isShow, id, onOpen, onClose]
}

const useShowControlNoteView = (): [
  number | null,
  number | null,
  boolean,
  (id: number, timelineId: number, isSharedValue: boolean) => void,
  () => void,
] => {
  const [id, setId] = useState<number | null>(null)
  const [timelineId, setTimelineId] = useState<number | null>(null)
  const [isShared, setIsShared] = useState<boolean>(false)

  const onOpen = useCallback((id: number, timelineId: number, isSharedValue: boolean) => {
    setId(id)
    setTimelineId(timelineId)
    setIsShared(isSharedValue)
  }, [])

  const onClose = useCallback(() => {
    setId(null)
    setTimelineId(null)
    setIsShared(false)
  }, [])

  return [id, timelineId, isShared, onOpen, onClose]
}

const useShowControlSearch = (
  initial: boolean = false,
): [
  boolean,
  string,
  FilterModel | null,
  (str: string, filter: FilterModel | null) => void,
  () => void,
] => {
  const [state, setState] = useState<boolean>(initial)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<FilterModel | null>(null)

  const onOpen = useCallback((str: string, filter: FilterModel | null) => {
    setSearch(str)
    setFilters(filter)
    setState(true)
  }, [])

  const onClose = useCallback(() => {
    setSearch('')
    setFilters(null)
    setState(false)
  }, [])

  return [state, search, filters, onOpen, onClose]
}

const useShowControlTeamMemberProfile = (initial: boolean = false) => {
  const [isOpen, setOpen] = useState<boolean>(initial)
  const [id, setId] = useState<number | null>(null)
  const [isCanEdit, setIsCanEdit] = useState(false)

  const onOpen = useCallback((id: number, isCanEdit: boolean) => {
    setOpen(true)
    setId(id)
    setIsCanEdit(isCanEdit)
  }, [])

  const onClose = useCallback(() => {
    setOpen(false)
    setId(null)
    setIsCanEdit(false)
  }, [])

  return { isOpen, id, isCanEdit, onOpen, onClose }
}

const ClientsDrawersProvider = (props: Props) => {
  const navigate = useNavigate()
  const {
    journeyItemType,
    journeyItemId,
    learnLibraryItemType,
    learnLibraryItemId,
    sharedItemType,
    sharedItemId,
  } = useParams()

  const [isOpenProfile, profileId, handleOpenProfile, handleCloseProfile] = useShowIDState()
  const [isOpenConcernProfile, handleOpenConcernProfile, handleCloseConcernProfile] =
    useShowControl()
  const teamsContext = useContext(TeamsContext)
  const concernProfileCarePersonId = teamsContext?.concernProfileCarePersonId ?? null

  const [idNoteView, noteTimelineIdView, isSharedNote, handleOpenNoteView, handleCloseNoteView] =
    useShowControlNoteView()
  const [isOpenNoteEdit, idNoteEdit, handleOpenNoteEdit, handleCloseNoteEdit] = useShowIDState()

  const [_, handleOpenQuestioning, handleCloseQuestionning, questioningProps] =
    useQuestioningControl()
  const [isOpenInviteMember, handleOpenInviteMember, handleCloseInviteMember] = useShowControl()
  const [isOpenContactUs, handleOpenContactUs, handleCloseContactUs] = useShowControl()
  const [isOpenAddAdmin, handleOpenAddAdmin, handleCloseAddAdmin] = useShowControl()
  const [isOpenAddMember, memberId, handleOpenAddMember, handleCloseAddMember] = useShowIDState()

  const { isAdmin, isCaregiver, isIndividualTrainee } = useAuthContext()

  const [
    isSearchDrawerOpen,
    searchDrawerText,
    searchFilters,
    handleOpenSearchDrawer,
    handleCloseSearchDrawer,
  ] = useShowControlSearch()
  const [
    isAdminSearchDrawerOpen,
    adminSearchDrawerText,
    adminSearchFilters,
    handleOpenAdminSearchDrawer,
    handleCloseAdminSearchDrawer,
  ] = useShowControlSearch()
  const [isAboutUsDrawerOpen, handleOpenAboutUsDrawer, handleCloseAboutUsDrawer] = useShowControl()
  const teamMemberDrawer = useShowControlTeamMemberProfile()
  const [
    isChangePasswordDrawerOpen,
    handleOpenChangePasswordDrawer,
    handleCloseChangePasswordDrawer,
  ] = useShowControl()
  const [isMySubscriptionOpen, handleOpenMySubscription, handleCloseMySubscription] =
    useShowControl()

  const handleCloseJourneyItem = useCallback(() => {
    navigate(routes.journey)
  }, [navigate])

  const handleCloseLearnLibraryItem = useCallback(() => {
    navigate(routes.learnLibrary)
  }, [navigate])

  const handleCloseSharedItem = useCallback(() => {
    navigate(routes.shared)
  }, [navigate])

  const isSharedNoteDeepLink = sharedItemType === 'note' && !!sharedItemId

  useEffect(() => {
    if (!isSharedNoteDeepLink || !sharedItemId) return

    let cancelled = false

    readSharedArticle({ id: Number(sharedItemId) })
      .then((response) => {
        if (cancelled) return
        const share = response.data
        if (share.noteId == null) {
          NotificationSys.showWarning("Can't load the shared note")
          navigate(routes.shared)
          return
        }
        handleOpenNoteView(share.noteId, share.id, true)
      })
      .catch(() => {
        if (cancelled) return
        NotificationSys.showWarning("Can't load the shared note")
        navigate(routes.shared)
      })

    return () => {
      cancelled = true
    }
  }, [isSharedNoteDeepLink, sharedItemId, handleOpenNoteView, navigate])

  const handleCloseSharedNoteView = useCallback(() => {
    handleCloseNoteView()
    if (isSharedNoteDeepLink) {
      navigate(routes.shared)
    }
  }, [handleCloseNoteView, isSharedNoteDeepLink, navigate])

  const value = useMemo(() => {
    return {
      handleOpenProfile,
      handleOpenConcernProfile,
      handleOpenNoteView,
      handleOpenNoteEdit,
      handleOpenQuestioning,
      handleOpenSearchDrawer,
      handleCloseSearchDrawer,
      handleOpenAdminSearchDrawer,
      handleCloseAdminSearchDrawer,
      handleOpenInviteMember,
      handleOpenAddMember,
      handleOpenAboutUsDrawer,
      handleOpenChangePasswordDrawer,
      handleOpenContactUs,
      handleOpenTeamMemberDrawer: teamMemberDrawer.onOpen,
      handleOpenMySubscription,
    }
  }, [
    handleOpenProfile,
    handleOpenConcernProfile,
    handleOpenNoteView,
    handleOpenNoteEdit,
    handleOpenQuestioning,
    handleOpenSearchDrawer,
    handleCloseSearchDrawer,
    handleOpenAdminSearchDrawer,
    handleCloseAdminSearchDrawer,
    handleOpenInviteMember,
    handleOpenAddMember,
    handleOpenAboutUsDrawer,
    handleOpenChangePasswordDrawer,
    handleOpenContactUs,
    teamMemberDrawer.onOpen,
    handleOpenMySubscription,
  ])

  return (
    <DrawersContext.Provider value={value}>
      {profileId && (
        <ProfileDrawer isOpen={isOpenProfile} handleClose={handleCloseProfile} userId={profileId} />
      )}
      {isOpenConcernProfile && concernProfileCarePersonId != null && (
        <PersonOfConcernProfileDrawer
          isOpen={isOpenConcernProfile}
          handleClose={handleCloseConcernProfile}
          carePersonId={concernProfileCarePersonId}
        />
      )}
      {idNoteView && (
        <ViewNoteDrawer
          id={idNoteView}
          timelineId={noteTimelineIdView}
          isShared={isSharedNote}
          handleClose={handleCloseSharedNoteView}
        />
      )}
      <EditNoteDrawer
        isOpen={isOpenNoteEdit}
        id={idNoteEdit}
        handleClose={handleCloseNoteEdit}
        handleOpen={handleOpenNoteEdit}
      />

      {journeyItemId && (
        <ViewArticleDrawer
          id={Number(journeyItemId)}
          type={journeyItemType as ArticleType}
          isOpen={true}
          handleClose={handleCloseJourneyItem}
        />
      )}

      {learnLibraryItemId && (
        <ViewArticleDrawer
          id={Number(learnLibraryItemId)}
          type={learnLibraryItemType as ArticleType}
          isOpen={true}
          handleClose={handleCloseLearnLibraryItem}
        />
      )}

      {sharedItemId && !isSharedNoteDeepLink && (
        <SharedArticleDrawer
          id={Number(sharedItemId)}
          type={sharedItemType as ArticleType}
          isOpen={true}
          handleClose={handleCloseSharedItem}
        />
      )}

      {!!questioningProps && (
        <QuestionWizard
          handleClose={handleCloseQuestionning}
          id={questioningProps.id}
          type={questioningProps.type}
          mode={questioningProps.mode}
          extraParams={questioningProps.extraParameters}
          onQuestioningTaken={questioningProps.onQuestioningTaken}
        />
      )}

      <InviteModal isOpen={isOpenInviteMember} handleClose={handleCloseInviteMember} />
      <ContactUsModal isOpen={isOpenContactUs} handleClose={handleCloseContactUs} />
      <AddMemberModal id={memberId} isOpen={isOpenAddMember} handleClose={handleCloseAddMember} />
      <SearchResultDrawer
        isOpen={isSearchDrawerOpen}
        searchQuery={searchDrawerText}
        searchFilters={searchFilters}
        handleClose={handleCloseSearchDrawer}
      />
      {isAdmin && (
        <AdminSearchResultDrawer
          isOpen={isAdminSearchDrawerOpen}
          searchQuery={adminSearchDrawerText}
          searchFilters={adminSearchFilters}
          handleClose={handleCloseAdminSearchDrawer}
        />
      )}
      <AboutUsDrawer isOpen={isAboutUsDrawerOpen} handleClose={handleCloseAboutUsDrawer} />
      <TeamMemberProfileDrawer
        isOpen={teamMemberDrawer.isOpen}
        id={teamMemberDrawer.id}
        handleClose={teamMemberDrawer.onClose}
        isCanEdit={teamMemberDrawer.isCanEdit}
      />
      <ChangePasswordDrawer
        isOpen={isChangePasswordDrawerOpen}
        handleClose={handleCloseChangePasswordDrawer}
      />
      {/* Same audience as the My Subscription menu item in ProfileList. */}
      {(isCaregiver || isIndividualTrainee) && (
        <MySubscriptionDrawer
          isOpen={isMySubscriptionOpen}
          handleClose={handleCloseMySubscription}
        />
      )}
      {props.children}
    </DrawersContext.Provider>
  )
}

export default ClientsDrawersProvider
